import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, ToggleButton, ToggleButtonGroup, Typography, Alert, CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BuildIcon from "@mui/icons-material/Build";
import api from "../../../services/auth";
import SegmentDefinitionEditor from "./SegmentDefinitionEditor";
import { validateUnomiCondition } from "../utils/unomiConditionUtils";
import { validateSegmentName, SEGMENT_NAME_MAX_LENGTH } from "../utils/segmentNameUtils";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const CreateSegmentDialog = ({ open, onClose, onCreated, brandName }) => {
    const navigate = useNavigate();
    const [mode, setMode] = useState("manual");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [condition, setCondition] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setMode("manual");
            setName("");
            setDescription("");
            setCondition(null);
            setError("");
        }
    }, [open]);

    const handleCreate = async () => {
        const nameValidation = validateSegmentName(name);
        if (!nameValidation.valid) {
            setError(nameValidation.error);
            return;
        }
        const cond = condition;
        if (!cond) {
            setError("Ajoutez au moins une condition.");
            return;
        }
        const validation = validateUnomiCondition(cond);
        if (!validation.valid) {
            setError(validation.error);
            return;
        }

        setSaving(true);
        setError("");
        try {
            const res = await api.post("/segments/create-manual", {
                name: name.trim(),
                description: description.trim() || undefined,
                condition: cond,
                create_mautic: true,
            });
            if (res.data?.success) {
                onCreated?.(res.data);
                onClose();
            } else {
                setError(res.data?.message || "Échec de la création du segment.");
            }
        } catch (e) {
            setError(e.response?.data?.detail || e.message || "Erreur lors de la création.");
        } finally {
            setSaving(false);
        }
    };

    const openAiChat = () => {
        onClose();
        navigate("/chat", {
            state: { prompt: "Crée un segment qui [donnez la description détaillée du segment]" },
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 600 }}>Nouveau segment</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, mt: 1 }}>
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={(_, v) => v && setMode(v)}
                        size="small"
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="manual" sx={{ textTransform: "none", gap: 0.5 }}>
                            <BuildIcon fontSize="small" /> Manuel
                        </ToggleButton>
                        <ToggleButton value="ai" sx={{ textTransform: "none", gap: 0.5 }}>
                            <SmartToyIcon fontSize="small" /> Assistant IA
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {mode === "ai" ? (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Créez un segment en décrivant vos critères en langage naturel via l&apos;assistant IA.
                            </Typography>
                            <Button variant="contained" onClick={openAiChat} sx={{ bgcolor: "#007B8A", textTransform: "none" }}>
                                Ouvrir l&apos;assistant IA
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <TextField
                                fullWidth label="Nom du segment" value={name}
                                onChange={(e) => setName(e.target.value)}
                                size="small" sx={{ ...fieldSx, mb: 2 }}
                                disabled={saving}
                                inputProps={{ maxLength: SEGMENT_NAME_MAX_LENGTH }}
                                helperText={`${name.length}/${SEGMENT_NAME_MAX_LENGTH} caractères — lettres, chiffres, espaces et tirets uniquement`}
                            />
                            <TextField
                                fullWidth label="Description" value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                size="small" multiline minRows={2}
                                sx={{ ...fieldSx, mb: 2 }}
                                disabled={saving}
                            />
                            <SegmentDefinitionEditor
                                condition={condition}
                                onConditionChange={setCondition}
                                brandName={brandName}
                                disabled={saving}
                            />
                        </>
                    )}
                </Box>
                {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>
            {mode === "manual" && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ bgcolor: "#007B8A", textTransform: "none" }}
                    >
                        {saving ? "Création…" : "Créer le segment"}
                    </Button>
                </DialogActions>
            )}
            {mode === "ai" && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} sx={{ textTransform: "none" }}>Fermer</Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default CreateSegmentDialog;
